import { useFriends, useDeals, useFeed, useHangs } from '../api/hooks';
import { useAuth } from '../api/useAuth';
import { Stack, Row, Text, Card, Panel, Avatar, Badge, AgentDot, List, ListItem, Money, Timestamp, EmptyState, Button, BlacklistAlertBadge, DealMenu } from '../ui';
import { Link } from 'react-router-dom';
import { STAGE_LABEL } from '../lib/types';

export function Pulse() {
  const { friend } = useAuth();
  const { data: friends, loading: lF } = useFriends();
  const { data: deals,   loading: lD, refresh: refreshDeals } = useDeals();
  const { data: events,  loading: lE } = useFeed();
  const { data: hangs,   loading: lH } = useHangs();

  const onlineFriends = friends.filter((f: any) => f.online);
  const blockers = deals.flatMap((d: any) => (d.blockers ?? []).map((b: any) => ({ ...b, deal: d })));
  const upcomingHangs = hangs.filter((h: any) => h.status === 'planned');
  const hotCount = deals.filter((d: any) => d.heat === 'hot').length;

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
      {/* On mobile, Today card shows first */}
      <Card className="md:hidden">
        <Stack gap={1}>
          <Text variant="micro">Today</Text>
          <Text variant="display">welcome, {friend?.name?.split(' ')[0] ?? 'friend'}</Text>
          <Text muted variant="small">
            {hotCount} hot {hotCount === 1 ? 'deal' : 'deals'} · {blockers.length} blocker{blockers.length === 1 ? '' : 's'} · {upcomingHangs.length} hang{upcomingHangs.length === 1 ? '' : 's'}
          </Text>
        </Stack>
      </Card>

      <aside className="md:col-span-3">
        <Stack gap={3}>
          <Panel title="Online">
            {lF ? <Text variant="label">loading…</Text>
              : onlineFriends.length === 0 ? <Text variant="label">no one online yet</Text>
              : <Stack gap={2}>
                  {onlineFriends.map((f: any) => (
                    <Row key={f.id} gap={2} align="center">
                      <Avatar name={f.name} friendId={f.id} hasAvatar={!!f.avatarUrl} size="sm" />
                      <Stack gap={0.5} className="flex-1 min-w-0">
                        <Text variant="small" className="truncate">{f.name}</Text>
                        <Text variant="label">{f.city ?? '—'}</Text>
                      </Stack>
                      <span className="h-1.5 w-1.5 rounded-full bg-positive" />
                    </Row>
                  ))}
                </Stack>}
          </Panel>

          <Panel title="Friends">
            {lF ? <Text variant="label">loading…</Text>
              : friends.length === 0
                ? <EmptyState title="no friends yet" hint="invite the first ones from settings" />
                : <Stack gap={1}>
                    <Text variant="small" mono>{friends.length}</Text>
                    <Text variant="label">in this circle</Text>
                  </Stack>}
          </Panel>
        </Stack>
      </aside>

      <section className="md:col-span-6">
        <Stack gap={3}>
          <Card className="hidden md:block">
            <Stack gap={1}>
              <Text variant="micro">Today</Text>
              <Text variant="display">welcome, {friend?.name?.split(' ')[0] ?? 'friend'}</Text>
              <Text muted variant="small">
                {hotCount} hot {hotCount === 1 ? 'deal' : 'deals'} · {blockers.length} active blocker{blockers.length === 1 ? '' : 's'} · {upcomingHangs.length} hang{upcomingHangs.length === 1 ? '' : 's'} planned
              </Text>
            </Stack>
          </Card>

          <Panel title="Activity" action={<AgentDot active label="6 agents on" />}>
            {lE ? <Text variant="label">loading…</Text>
              : events.length === 0
                ? <EmptyState title="no activity yet" hint="agent posts will appear here as deals move" />
                : <Stack gap={2}>
                    {events.slice(0, 10).map((e: any) => (
                      <div key={e.id} className="hairline-b last:border-b-0 pb-2 last:pb-0">
                        <Row justify="between">
                          <Text variant="micro">{e.action}</Text>
                          <Timestamp date={new Date(e.at)} />
                        </Row>
                        {e.subjectType && (
                          <Text variant="label" className="mt-1">{e.subjectType} · {e.subjectId?.slice(0, 8)}</Text>
                        )}
                      </div>
                    ))}
                  </Stack>}
          </Panel>

          <Panel title="Active deals">
            {lD ? <Text variant="label">loading…</Text>
              : deals.length === 0
                ? <EmptyState title="no deals yet" hint="create your first deal to start tracking" action={<Link to="/deals/new"><Button variant="primary" size="sm">+ new deal</Button></Link>} />
                : <List>
                    {deals.filter((d: any) => d.stage !== 'closed' && d.stage !== 'killed').map((d: any) => (
                      <ListItem key={d.id}>
                        <Row justify="between" align="center">
                          <Link to={`/deals/${d.id}`} className="flex-1 min-w-0 block">
                          <Row justify="between">
                            <Stack gap={0.5}>
                              <Row gap={1}>
                                <Text variant="small" className="font-medium">{d.name}</Text>
                                {d.heat && <Badge tone={d.heat === 'hot' ? 'negative' : d.heat === 'warm' ? 'warning' : 'neutral'}>{d.heat}</Badge>}
                                {d.listFlagStatus === 'blacklist_hit' && <BlacklistAlertBadge summary={d.listFlagSummary} />}
                              </Row>
                              <Row gap={1}>
                                <Text variant="label">{d.geography ?? '—'}</Text>
                                <span className="text-muted">·</span>
                                <Text variant="label">{STAGE_LABEL[d.stage as keyof typeof STAGE_LABEL] ?? d.stage}</Text>
                              </Row>
                            </Stack>
                            {d.ticketSize && <Money amount={d.ticketSize} currency={d.currency || 'USD'} className="text-sm" />}
                            </Row>
                          </Link>
                          <DealMenu dealId={d.id} state="active" onChange={refreshDeals} className="ml-2" />
                        </Row>
                      </ListItem>
                    ))}
                  </List>}
          </Panel>
        </Stack>
      </section>

      <aside className="md:col-span-3">
        <Stack gap={3}>
          <Panel title="Reminders">
            {blockers.length === 0
              ? <Text variant="label">nothing pending</Text>
              : <Stack gap={2}>
                  {blockers.slice(0, 5).map((b: any) => (
                    <Row key={b.id} gap={1} align="start">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-negative shrink-0" />
                      <Stack gap={0.5} className="flex-1 min-w-0">
                        <Text variant="small">{b.description}</Text>
                        <Text variant="label">{b.deal.name}</Text>
                      </Stack>
                    </Row>
                  ))}
                </Stack>}
          </Panel>

          <Panel title="Upcoming hangs">
            {lH ? <Text variant="label">loading…</Text>
              : upcomingHangs.length === 0
                ? <Text variant="label">nothing planned</Text>
                : <Stack gap={2}>
                    {upcomingHangs.map((h: any) => (
                      <Stack key={h.id} gap={1}>
                        <Row gap={1}>
                          <Text variant="small" className="font-medium">{h.city}</Text>
                          <Timestamp date={new Date(h.startsAt)} />
                        </Row>
                        {h.venue && <Text variant="label">{h.venue}</Text>}
                      </Stack>
                    ))}
                  </Stack>}
          </Panel>
        </Stack>
      </aside>
    </div>
  );
}
